export function serialize(root: HTMLElement) {
  let result = "";

  root.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      result += node.textContent;
    }

    if (
      node.nodeType === Node.ELEMENT_NODE &&
      (node as HTMLElement).dataset.mention
    ) {
      const el = node as HTMLElement;
      const meta = el.dataset.mention!;
      const label = el.innerText.replace(/^@/, "");
      result += `@[${label}](${meta})`;
    }
  });

  return result;
}

/** Serialized mention token: `@[mentionType:identifier:label]` (see ChatInput post-processing). */
const SERIALIZED_MENTION_PATTERN = /@\[([^:\]]+):([^:\]]+):([^\]]*)\]/;

export type ParsedMessageMention = {
  id: string;
  type: string;
};

function normalizeMentionTypeForPayload(raw: string): string {
  const t = raw.trim().toLowerCase();
  switch (t) {
    case "user":
      return "user";
    case "contact":
      return "contact";
    case "assets":
      return "asset";
    case "people":
      return "people";
    case "stage":
    case "stages":
      return "stage";
    case "action":
    case "actions":
      return "action";
    default:
      return t;
  }
}

/** Returns `@[type:id:label]` mentions in order; only the first occurrence per `id` is kept. */
export function extractMentionsFromMessage(message: string): ParsedMessageMention[] {
  const out: ParsedMessageMention[] = [];
  const seenIds = new Set<string>();
  const re = new RegExp(SERIALIZED_MENTION_PATTERN.source, "g");
  let match: RegExpExecArray | null;
  while ((match = re.exec(message)) !== null) {
    const id = match[2].trim();
    if (!id || seenIds.has(id)) continue;
    seenIds.add(id);
    out.push({
      id,
      type: normalizeMentionTypeForPayload(match[1]),
    });
  }
  return out;
}


/**
 * Each row is `{ entityType, [entityType]: { id, name?, icon? } }`.
 * Returns only id, display name, and optional icon thumbnail.
 */
export function extractDealRoomSearchEntity(row: unknown): {
  id: string;
  name: string;
  iconThumbnail?: string;
  /** Present on `people` rows when API sends `type: "user" | "contact"`. */
  peopleMentionKind?: "user" | "contact";
} | null {
  if (!row || typeof row !== "object") return null;
  const r = row as Record<string, unknown>;
  const entityType = String(r.entityType ?? "").toLowerCase();
  if (!entityType) return null;

  const nested = r[entityType];
  if (!nested || typeof nested !== "object") return null;
  const n = nested as Record<string, unknown>;

  const id = n.id;
  const name = n.name;
  if (typeof id !== "string") return null;

  const label = typeof name === "string" ? name : "";

  const icon = n.icon;
  let iconThumbnail: string | undefined;
  if (icon && typeof icon === "object") {
    const thumb = (icon as Record<string, unknown>).thumbnail;
    if (typeof thumb === "string") iconThumbnail = thumb;
  }

  let peopleMentionKind: "user" | "contact" | undefined;
  if (entityType === "people") {
    const pt = n.type;
    if (pt === "user" || pt === "contact") {
      peopleMentionKind = pt;
    }
  }

  return { id, name: label, iconThumbnail, peopleMentionKind };
}